/**
 * Sprint P19.2 · Feb 2026 · Mine Design Workspace  ·  Open-cast only (P22).
 *
 * Distinct Conceptual (pseudo-pit) vs Engineering (real bench-by-bench pit)
 * sub-panels. Underground design has been removed from the UI per the
 * enterprise directive — SRES is now focused on the open-pit workflow.
 * Backend underground endpoints remain for archive compatibility but are
 * not surfaced anywhere in the frontend.
 */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Mountains, DownloadSimple, Play,
  Warning, Info, CubeTransparent,
} from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  getMineDesignSummary,
  computeEngineeringPit, getEngineeringPit,
  downloadMineDesignArtifact,
} from "@/lib/api";
import { WizardCard, StatusRow, Pill, LoadingBanner, KV } from "@/pages/wizard/_shared";


// ═══════════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════════
function fmt(n, digits = 0) {
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return Number(n).toLocaleString(undefined, {
    maximumFractionDigits: digits, minimumFractionDigits: 0,
  });
}


function ParamInput({ label, value, onChange, step = 1, testId, unit, min }) {
  return (
    <div className="space-y-0.5">
      <Label className="text-[9px] uppercase tracking-widest font-mono text-slate-500">
        {label}{unit && <> · <span className="text-slate-400">{unit}</span></>}
      </Label>
      <Input type="number" step={step} min={min} value={value ?? ""}
              onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
              className="h-8 text-[12px] font-mono"
              data-testid={testId} />
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Engineering Open Pit sub-panel
// ═══════════════════════════════════════════════════════════════════
function EngineeringPitPanel({ projectId }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const pitQ = useQuery({
    queryKey: ["mine-pit", projectId],
    queryFn:  () => getEngineeringPit(projectId).catch(() => null),
    enabled:  !!projectId,
  });
  const [p, setP] = useState({
    bench_height_m:       10,
    bench_face_angle_deg: 70,
    overall_slope_deg:    45,
    berm_width_m:         6,
    ramp_width_m:         25,
    ramp_grade:           0.10,
    buffer_m:             20,
    max_depth_m:          null,
    ore_density:          2.7,
    waste_density:        2.5,
    corner_smoothing:     32,
    include_ramp_helix:   true,
  });

  const compute = useMutation({
    mutationFn: () => computeEngineeringPit(projectId, p),
    onSuccess:  (d) => {
      toast.success(`Pit built — ${d.n_benches} benches, ${fmt(d.totals?.ore_tonnes / 1e6, 2)} Mt ore`);
      qc.invalidateQueries({ queryKey: ["mine-pit", projectId] });
      qc.invalidateQueries({ queryKey: ["mine-design-summary", projectId] });
    },
    onError:    (e) => {
      const detail = e?.response?.data?.detail;
      toast.error(detail?.message || detail || e.message || "Pit build failed");
    },
  });

  const dl = useMutation({
    mutationFn: (name) => downloadMineDesignArtifact(projectId, name),
    onSuccess:  (_, name) => toast.success(`Downloaded ${name}`),
    onError:    (e) => toast.error(e?.response?.data?.detail || e.message),
  });

  const pit = pitQ.data;
  return (
    <WizardCard
        title="Engineering · Open-Pit Design"
        subtitle="Bench-by-bench crest/toe polylines + ramp helix, cut against the block model"
        testId="w-eng-pit"
        right={
          <div className="flex gap-1">
            {pit && (
              <>
                <Button size="sm" variant="outline" className="h-7 text-[11px]"
                          onClick={() => nav(`/app/projects/${projectId}/cockpit?overlay=mine-design&kind=pit`)}
                          data-testid="w-eng-pit-view-3d">
                  <CubeTransparent size={11} className="mr-1" /> View in 3D
                </Button>
                <Button size="sm" variant="outline" className="h-7 text-[11px]"
                          onClick={() => dl.mutate("pit.csv")}
                          data-testid="w-eng-pit-dl-csv">
                  <DownloadSimple size={11} className="mr-1" /> CSV
                </Button>
                <Button size="sm" variant="outline" className="h-7 text-[11px]"
                          onClick={() => dl.mutate("pit.dxf")}
                          data-testid="w-eng-pit-dl-dxf">
                  <DownloadSimple size={11} className="mr-1" /> DXF
                </Button>
                <Button size="sm" variant="outline" className="h-7 text-[11px]"
                          onClick={() => dl.mutate("pit.obj")}
                          data-testid="w-eng-pit-dl-obj">
                  <DownloadSimple size={11} className="mr-1" /> OBJ
                </Button>
              </>
            )}
            <Button size="sm" className="h-7 text-[11px] bg-brand-blue text-white"
                       disabled={compute.isPending}
                       onClick={() => compute.mutate()}
                       data-testid="w-eng-pit-build">
              <Play size={11} className="mr-1" />
              {compute.isPending ? "Building…" : (pit ? "Rebuild pit" : "Build pit")}
            </Button>
          </div>
        }>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-3">
        <ParamInput label="Bench H" unit="m"      value={p.bench_height_m}
                     onChange={(v) => setP({ ...p, bench_height_m: v })}
                     step="0.5" testId="w-eng-pit-bench-h" />
        <ParamInput label="Face angle" unit="°"   value={p.bench_face_angle_deg}
                     onChange={(v) => setP({ ...p, bench_face_angle_deg: v })}
                     testId="w-eng-pit-face" />
        <ParamInput label="Overall slope" unit="°" value={p.overall_slope_deg}
                     onChange={(v) => setP({ ...p, overall_slope_deg: v })}
                     testId="w-eng-pit-slope" />
        <ParamInput label="Berm width" unit="m"   value={p.berm_width_m}
                     onChange={(v) => setP({ ...p, berm_width_m: v })}
                     testId="w-eng-pit-berm" />
        <ParamInput label="Ramp grade" unit="rise/run" value={p.ramp_grade}
                     onChange={(v) => setP({ ...p, ramp_grade: v })} step="0.01"
                     testId="w-eng-pit-ramp-grade" />
        <ParamInput label="Buffer" unit="m"       value={p.buffer_m}
                     onChange={(v) => setP({ ...p, buffer_m: v })}
                     testId="w-eng-pit-buffer" />
      </div>

      {pitQ.isLoading && <LoadingBanner label="Loading pit design…" />}
      {!pitQ.isLoading && !pit && (
        <StatusRow tone="info">
          <b>No engineering pit yet.</b> Adjust parameters above and click <b>Build pit</b>.
        </StatusRow>
      )}

      {pit && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            <KV k="Benches"     v={pit.n_benches}                    testId="w-eng-pit-kpi-benches" />
            <KV k="Ore Mt"      v={fmt(pit.totals?.ore_tonnes / 1e6, 2)}   testId="w-eng-pit-kpi-ore" />
            <KV k="Waste Mt"    v={fmt(pit.totals?.waste_tonnes / 1e6, 2)} testId="w-eng-pit-kpi-waste" />
            <KV k="Strip ratio" v={pit.totals?.strip_ratio != null ? pit.totals.strip_ratio.toFixed(2) + " : 1" : "—"} testId="w-eng-pit-kpi-strip" />
            <KV k="Contained oz" v={fmt(pit.totals?.contained_ounces, 0)}  testId="w-eng-pit-kpi-oz" />
            <KV k="Avg grade" v={((pit.totals?.avg_grade ?? 0)).toFixed(3) + " g/t"} testId="w-eng-pit-kpi-grade" />
          </div>

          <div className="text-[10px] italic text-slate-500 border-l-2 border-amber-300 pl-2">
            <Info size={11} className="inline mr-1 -mt-0.5" /> {pit.disclaimer}
          </div>

          <div className="rounded border border-slate-200 overflow-x-auto max-h-72">
            <table className="w-full text-[11px] font-mono">
              <thead className="bg-slate-100 text-slate-700 sticky top-0">
                <tr>
                  <th className="text-left px-2 py-1 w-14">Bench</th>
                  <th className="text-right px-2 py-1 w-24">RL top</th>
                  <th className="text-right px-2 py-1 w-24">RL bot</th>
                  <th className="text-right px-2 py-1 w-20">Ore blk</th>
                  <th className="text-right px-2 py-1 w-24">Ore t</th>
                  <th className="text-right px-2 py-1 w-24">Waste t</th>
                  <th className="text-right px-2 py-1 w-24">Grade</th>
                  <th className="text-right px-2 py-1 w-20">Strip</th>
                </tr>
              </thead>
              <tbody>
                {pit.benches?.map((b) => (
                  <tr key={b.bench} className="border-t border-slate-200"
                        data-testid={`w-eng-pit-bench-${b.bench}`}>
                    <td className="px-2 py-1 font-bold">{b.bench}</td>
                    <td className="text-right px-2 py-1">{b.elev_top}</td>
                    <td className="text-right px-2 py-1">{b.elev_bot}</td>
                    <td className="text-right px-2 py-1">{b.ore_blocks}</td>
                    <td className="text-right px-2 py-1">{fmt(b.ore_tonnes / 1e3, 1)}k</td>
                    <td className="text-right px-2 py-1">{fmt(b.waste_tonnes / 1e3, 1)}k</td>
                    <td className="text-right px-2 py-1">{b.avg_grade?.toFixed(3) || "—"}</td>
                    <td className="text-right px-2 py-1">{b.strip_ratio ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </WizardCard>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Master workspace  ·  Open-cast only
// ═══════════════════════════════════════════════════════════════════
export default function MineDesignWorkspace({ projectId }) {
  const summaryQ = useQuery({
    queryKey: ["mine-design-summary", projectId],
    queryFn:  () => getMineDesignSummary(projectId),
    enabled:  !!projectId,
    refetchInterval: 60_000,
  });
  const s = summaryQ.data || {};
  return (
    <div className="space-y-4" data-testid="w-mine-design-workspace">
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded border border-slate-200 p-2 bg-white flex items-center gap-2"
              data-testid="w-mine-design-status-conceptual">
          <Mountains size={14} className="text-emerald-500" />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500">Conceptual pit</div>
            <div className="text-[12px] font-bold text-slate-800">
              {s.conceptual_pit ? "✓ Present" : "— not built"}
            </div>
          </div>
        </div>
        <div className="rounded border border-slate-200 p-2 bg-white flex items-center gap-2"
              data-testid="w-mine-design-status-eng-pit">
          <Mountains size={14} className="text-sky-500" />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500">Engineering open-pit</div>
            <div className="text-[12px] font-bold text-slate-800">
              {s.engineering_pit
                ? `✓ ${s.pit_benches} benches · ${fmt((s.pit_totals?.ore_tonnes || 0) / 1e6, 2)} Mt ore`
                : "— not built"}
            </div>
          </div>
        </div>
      </div>

      <StatusRow tone="warn">
        <Warning size={12} className="inline mr-1 -mt-0.5" />
        <b>Open-cast only:</b> Conceptual pit is a visualisation-only geometric
        envelope. Engineering pit is a bench-by-bench design cut against the
        approved block model. Neither replaces a fully-optimised pit-shell
        (Lerchs-Grossmann) — detailed geotechnical, haulage and metallurgy
        studies are required before construction.
      </StatusRow>

      <EngineeringPitPanel projectId={projectId} />
    </div>
  );
}
